import { Form, Modal, Tabs } from 'antd';
import { useIntl } from 'react-intl';
import InvoiceDocReferencedSearchModal from './InvoiceDocReferencedSearchModal';
import InvoiceDocReferencedAccessKeyForm from './InvoiceDocReferencedAccessKeyForm';
import InvoiceDocReferencedIssuerForm from './InvoiceDocReferencedIssuerForm';
import { useDisclosure } from '@/hooks';
import {
  InvoiceDocReferencedFormValues,
  InvoiceDocReferencedIssuerType,
  InvoiceDocReferencedPayload,
} from '@/core/domain/invoice/referencedDocument/referencedDocument.types';
import {
  useCreateReferencedDocument,
  useGetInvoiceDocReferencedId,
  useUpdateInvoiceDocReferenced,
} from '@/core/domain/invoice/referencedDocument/referencedDocument.hooks';
import { useRouteMatch } from 'react-router-dom';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { DateTimeFormat } from '@/utils/moment';

interface Props {
  isEdit?: boolean;
  visible: boolean;
  documentId?: number;
  onClose: () => void;
}

type SelectedTab = 'NF-e' | 'Invoice';

export default function InvoiceDocReferencedModal({
  isEdit,
  visible,
  onClose,
  documentId,
}: Props) {
  const intl = useIntl().formatMessage;

  const [form] = Form.useForm<InvoiceDocReferencedFormValues>();

  const [selectedTab, setSelectedTab] = useState<SelectedTab>('NF-e');
  const [modalSearchVisible, modalSearchActions] = useDisclosure();
  const [issuerType, setIssuerType] =
    useState<InvoiceDocReferencedIssuerType>('Cliente');

  const { invoiceId } = useRouteMatch<{ invoiceId: string }>().params;

  const createReferencedDocument = useCreateReferencedDocument();
  const updateInvoiceDocReferenced = useUpdateInvoiceDocReferenced();

  const { mutateAsync, data } = useGetInvoiceDocReferencedId();

  const handleOk = async () => {
    const { issuerType, issuerId, ...values } = await form.validateFields();

    const obj: InvoiceDocReferencedPayload = {
      ...values,
      id: documentId ?? undefined,
      emissionDate: moment(values.emissionDate).format(DateTimeFormat),
      invoiceId: Number(invoiceId),
      customerId: issuerType === 'Cliente' ? issuerId : undefined,
      insurerId: issuerType === 'Seguradora' ? issuerId : undefined,
      supplierId: issuerType === 'Fornecedor' ? issuerId : undefined,
    };

    if (isEdit) {
      updateInvoiceDocReferenced.mutateAsync(obj);
    } else {
      createReferencedDocument.mutateAsync(obj);
    }

    onClose();
  };

  const handleOkKey = async () => {
    const valuesKey = await form.validateFields();

    const obj: InvoiceDocReferencedPayload = {
      id: data?.id,
      key: `${valuesKey.key}`,
      invoiceId: Number(invoiceId),
    };

    if (isEdit) {
      updateInvoiceDocReferenced.mutateAsync({
        ...obj,
        emissionDate: moment(data?.emissionDate).format(DateTimeFormat),
      });
    } else {
      createReferencedDocument.mutateAsync(obj);
    }

    onClose();
  };

  const handleIssuerSelect = (name: string, id: number) => {
    form.setFieldsValue({ issuer: name, issuerId: +id });
    modalSearchActions.toggle();
  };

  useEffect(() => {
    if (documentId) {
      mutateAsync({ invoiceId: +invoiceId, id: documentId });
    }
  }, [documentId]);

  useEffect(() => {
    if (data) {
      const {
        key,
        emissionDate,
        recipientName,
        ufIbge,
        customerId,
        insurerId,
        supplierId,
        ...restValue
      } = data;

      if (key) {
        form?.setFieldValue('key', key);
      } else {
        setIssuerType(
          customerId ? 'Cliente' : insurerId ? 'Seguradora' : 'Fornecedor',
        );
        form?.setFieldsValue({
          ...restValue,
          issuerType: customerId
            ? 'Cliente'
            : insurerId
            ? 'Seguradora'
            : 'Fornecedor',
          issuerId: customerId
            ? customerId
            : insurerId
            ? insurerId
            : supplierId,
          issuer: recipientName,
          ufCode: ufIbge,
          emissionDate: moment(emissionDate),
        });
      }
    }
  }, [data, isEdit]);

  return (
    <Modal
      open={visible}
      title={intl({ id: 'layout.menu.fiscal.invoice.referenced' })}
      onOk={isEdit && !!data?.key ? handleOkKey : handleOk}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      okText={intl({ id: 'layout.menu.fiscal.invoice.table.save' })}
      cancelText={intl({ id: 'layout.menu.fiscal.invoice.table.cancel' })}
      destroyOnClose
      width={'50%'}
    >
      {isEdit && !!data?.key && (
        <InvoiceDocReferencedAccessKeyForm
          selectedTab={selectedTab}
          form={form}
        />
      )}

      {isEdit && !data?.key && (
        <InvoiceDocReferencedIssuerForm
          form={form}
          issuerType={issuerType}
          setIssuerType={setIssuerType}
          openSearch={modalSearchActions.toggle}
        />
      )}
      {!isEdit && (
        <Tabs
          defaultActiveKey={selectedTab}
          activeKey={selectedTab}
          onChange={(tab) => {
            setSelectedTab(tab as SelectedTab);
            form?.resetFields();
          }}
        >
          <Tabs.TabPane tab="NF-e" key="NF-e">
            {selectedTab === 'NF-e' && (
              <InvoiceDocReferencedAccessKeyForm
                selectedTab={selectedTab}
                form={form}
              />
            )}
          </Tabs.TabPane>

          <Tabs.TabPane tab="Nota Fiscal" key="Invoice">
            {selectedTab === 'Invoice' && (
              <InvoiceDocReferencedIssuerForm
                form={form}
                issuerType={issuerType}
                setIssuerType={setIssuerType}
                openSearch={modalSearchActions.toggle}
              />
            )}
          </Tabs.TabPane>
        </Tabs>
      )}
      <InvoiceDocReferencedSearchModal
        search={modalSearchVisible}
        onClose={modalSearchActions.toggle}
        handleSelect={handleIssuerSelect}
        type={form.getFieldValue('issuerType')}
      />
    </Modal>
  );
}
